import { useState, useEffect, useRef } from 'react';

import PropTypes from 'prop-types';
import {
	BsFunnel,
	BsBuilding,
	BsSearch,
	BsGeoAlt,
	BsFileEarmarkText,
	BsCashStack,
	BsPeople,
	BsCalendarEvent,
	BsCheckCircle,
	BsInfoCircle,
} from 'react-icons/bs';

const fmtCount = (n) => Math.round(n || 0).toLocaleString('de-DE');
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Backend stage key → display metadata. The "base" stage is the searchable
// Handelsregister universe; each later stage is one active criterion.
const STAGE_META = {
	base: { icon: BsBuilding, title: 'Gesamte Datenbank · Deutschland', sub: 'Alle eingetragenen Firmen' },
	branche: { icon: BsSearch, title: 'Branche (WZ-Codes)', sub: 'Branchencodes' },
	standort: { icon: BsGeoAlt, title: 'Standort', sub: 'Ort, Bundesland, PLZ & Umkreis' },
	rechtsform: { icon: BsFileEarmarkText, title: 'Rechtsform', sub: 'Rechtsform' },
	umsatz: { icon: BsCashStack, title: 'Umsatz', sub: 'Umsatzbereich' },
	bilanzsumme: { icon: BsCashStack, title: 'Bilanzsumme', sub: 'Bilanzbereich' },
	gewinn: { icon: BsCashStack, title: 'Gewinn', sub: 'Gewinnbereich' },
	mitarbeiter: { icon: BsPeople, title: 'Mitarbeiteranzahl', sub: 'Mitarbeiterbereich' },
	ereignis: { icon: BsCalendarEvent, title: 'Ereignis', sub: 'Handelsregister-Ereignis' },
	weitere: { icon: BsFunnel, title: 'Weitere Filter', sub: 'Ausschluss & Website' },
};

// Animated count-up so the headline number visibly reacts to criterion changes.
const useCountUp = (target, ms = 480) => {
	const [val, setVal] = useState(target);
	const fromRef = useRef(target);
	const rafRef = useRef(null);

	useEffect(() => {
		const from = fromRef.current;
		const start = performance.now();
		cancelAnimationFrame(rafRef.current);
		const tick = (now) => {
			const t = Math.min(1, (now - start) / ms);
			const eased = 1 - Math.pow(1 - t, 3);
			setVal(Math.round(from + (target - from) * eased));
			if (t < 1) rafRef.current = requestAnimationFrame(tick);
			else fromRef.current = target;
		};
		rafRef.current = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(rafRef.current);
	}, [target, ms]);

	return val;
};

const FreeBadge = () => (
	<div className="flex items-center gap-1.5 text-[10.5px] text-success-700">
		<BsCheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
		<span>Kostenlose Vorschau · keine Credits</span>
	</div>
);

// Empty / prompt state — shown before any criterion narrows the base set.
const PreviewPrompt = () => (
	<div className="rounded-lg border border-dashed border-gray-300 bg-gray-50/60 px-3 py-3 flex items-start gap-2">
		<BsFunnel className="w-4 h-4 text-bgBase-800 mt-0.5 flex-shrink-0" />
		<p className="text-[11.5px] text-bgBase-800 leading-relaxed m-0">
			Bei Handelsregister-Suchen erstellen wir eine Vorschau der verfügbaren Firmenzahl auf basis der eingegebenen
			Kriterien
		</p>
	</div>
);

// Maps searches have no searchable base → explain instead of faking a number.
const MapsHint = () => (
	<div className="rounded-lg border border-dashed border-gray-300 bg-gray-50/60 px-3 py-3 flex items-start gap-2">
		<BsInfoCircle className="w-4 h-4 text-bgBase-800 mt-0.5 flex-shrink-0" />
		<p className="text-[11.5px] text-bgBase-800 leading-relaxed m-0">
			Die Treffer-Vorschau ist nur für die <span className="font-medium text-text">Handelsregister-Suche</span>{' '}
			verfügbar. Die Karten-Suche ermittelt die Treffer erst beim Start.
		</p>
	</div>
);

const LoadingPreview = ({ label = 'Treffer werden berechnet …' }) => (
	<div className="rounded-lg border border-primary-200 bg-white px-3 py-4 flex items-center justify-center gap-2">
		<span className="w-4 h-4 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
		<span className="text-xs text-bgBase-800">{label}</span>
	</div>
);

LoadingPreview.propTypes = { label: PropTypes.string };

/**
 * Builds the funnel view-model (labels, per-stage removed counts, log-scaled bar
 * fractions) from the backend's { total, stages } response.
 *
 * @param {Array<{key: string, count: number}>} stages
 * @returns {{ rows: object[], final: number, base: number }|null}
 */
const buildFunnelRows = (stages) => {
	if (!Array.isArray(stages) || stages.length === 0) return null;
	const base = stages[0]?.count || 0;
	const logBase = Math.log(Math.max(2, base));
	let prev = base;
	const rows = stages.map((stage, i) => {
		const meta = STAGE_META[stage.key] || { icon: BsFunnel, title: stage.key, sub: '' };
		const removed = i === 0 ? 0 : Math.max(0, prev - stage.count);
		prev = stage.count;
		const frac = clamp(Math.log(Math.max(2, stage.count)) / logBase, 0.18, 1);
		return { ...stage, ...meta, removed, frac };
	});
	return { rows, final: stages[stages.length - 1].count, base };
};

// Variant A — stepped funnel list
const FunnelListPreview = ({ funnel }) => {
	const final = useCountUp(funnel.final);
	return (
		<div className="rounded-lg border border-primary-200 bg-white overflow-hidden">
			<div className="px-3 pt-3 pb-2.5 bg-gradient-to-br from-primary-100 to-pink-50 border-b border-primary-100">
				<div className="flex items-center justify-between">
					<span className="text-[11px] font-semibold text-bgBase-800 uppercase tracking-wide">
						Voraussichtliche Treffer
					</span>
					<BsFunnel className="w-3.5 h-3.5 text-primary-400" />
				</div>
				<div className="flex items-baseline gap-1.5 mt-0.5">
					<span className="text-lg font-extrabold text-primary-700 tabular-nums leading-none">
						{fmtCount(final)}
					</span>
					<span className="text-xs text-bgBase-800">Firmen</span>
				</div>
			</div>
			<div className="px-3 py-2.5 space-y-1.5">
				{funnel.rows.map((s, i) => {
					const isLast = i === funnel.rows.length - 1;
					const Icon = s.icon;
					return (
						<div key={s.key} className="group">
							<div className="flex items-center gap-2">
								<Icon
									className={`w-3.5 h-3.5 flex-shrink-0 ${isLast ? 'text-primary-600' : 'text-bgBase-800'}`}
								/>
								<span
									className={`text-[11.5px] truncate flex-1 ${isLast ? 'font-semibold text-text' : 'text-text'}`}
									title={s.title}
								>
									{s.title}
								</span>
								<span
									className={`text-[11.5px] tabular-nums font-semibold ${isLast ? 'text-primary-700' : 'text-text'}`}
								>
									{fmtCount(s.count)}
								</span>
							</div>
							<div className="mt-1 ml-5 flex items-center gap-2">
								<div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
									<div
										className={`h-full rounded-full transition-all duration-500 ${isLast ? 'bg-primary-500' : 'bg-primary-300'}`}
										style={{ width: `${Math.round(s.frac * 100)}%` }}
									/>
								</div>
								{i > 0 && (
									<span className="text-[10px] text-bgBase-800 tabular-nums w-14 text-right">
										−{fmtCount(s.removed)}
									</span>
								)}
							</div>
						</div>
					);
				})}
			</div>
			<div className="px-3 pb-2.5">
				<FreeBadge />
			</div>
		</div>
	);
};

FunnelListPreview.propTypes = {
	funnel: PropTypes.shape({
		final: PropTypes.number.isRequired,
		rows: PropTypes.array.isRequired,
	}).isRequired,
};

/**
 * Live HR result-count preview ("Treffer-Trichter").
 *
 * Renders nothing meaningful for Maps searches (a hint instead). For HR, shows a
 * prompt until at least one criterion is active, a spinner while the first count
 * loads, then the stepped funnel fed by real backend counts.
 */
export const SearchPreview = ({ searchType, hasCriteria, isLoading, isResolving, data }) => {
	if (searchType !== 'hr') return <MapsHint />;
	if (!hasCriteria) return <PreviewPrompt />;

	// Free-text branch terms go through the AI resolver first (~1-3s); a stale
	// funnel would show pre-resolution numbers, so show the resolving state instead.
	if (isResolving) return <LoadingPreview label="Branche wird analysiert …" />;
	if (isLoading) return <LoadingPreview />;

	const funnel = buildFunnelRows(data?.stages);
	// Only the base stage (no narrowing criteria counted) → still a prompt.
	if (!funnel || funnel.rows.length <= 1) return <PreviewPrompt />;

	return <FunnelListPreview funnel={funnel} />;
};

SearchPreview.propTypes = {
	searchType: PropTypes.oneOf(['maps', 'hr']).isRequired,
	hasCriteria: PropTypes.bool.isRequired,
	isLoading: PropTypes.bool,
	isResolving: PropTypes.bool,
	data: PropTypes.shape({
		total: PropTypes.number,
		stages: PropTypes.array,
		available: PropTypes.bool,
	}),
};
